<template>
    <div data-test-id="l-reviews-edit-confirm-manager-form">
        <p v-show="reviewCycle.createdAsRole == 'manager'">
            <l-text :text="$t(`reviews.admin.kickoffAsManager`)" />
        </p>

        <p v-show="reviewCycle.createdAsRole == 'employee'">
            <l-text :text="$t(`reviews.admin.kickoffAsEmployee`)" />
        </p>

        <hr />

        <h4 class="reviewSetupHeading color-dark">
            <strong>{{ $t(`reviews.admin.cycleName`) }}</strong>
        </h4>

        <div class="row">
            <div class="col-md-5">
                <l-input-text
                    v-model="reviewCycle.name"
                    style="margin-top: 0px"
                    :label="``"
                    :placeholder="$t(`reviews.admin.placeholderTitle`)"
                />
            </div>
        </div>

        <div v-show="reviewCycle.createdAsRole == 'manager'">
            <hr />

            <h4 class="reviewSetupHeading color-dark">
                <strong>{{ $t(`reviews.admin.cycleParticipants`) }}</strong>
            </h4>

            <div class="row">
                <div class="col-md-5">
                    <l-input-typeahead
                        style="margin-top: 0px"
                        :label="``"
                        :placeholder="$t(`reviews.admin.participationSpecificIncludeAsManager`)"
                        :data="directReports"
                        :on-select="item => addParticipationRule(item, 'include')"
                        :on-input-change="onTypeaheadParticipantInputChange"
                    />
                </div>
                <div
                    v-if="reviewCycle.managerReviewsEnabled && reviewCycle.managerOverwritten"
                    class="col-md-4"
                >
                    <l-input-typeahead
                        v-if="
                            !(
                                reviewCycle.managerOverwrittenBy &&
                                reviewCycle.managerOverwrittenBy._id
                            )
                        "
                        style="margin-top: 0px"
                        :label="``"
                        :placeholder="$t(`reviews.admin.placeholderManagerOverwrite`)"
                        :data="users"
                        :on-select="addManagerOverwrite"
                        :on-input-change="onTypeaheadUserInputChange"
                    />

                    <div
                        v-if="
                            reviewCycle.managerOverwrittenBy && reviewCycle.managerOverwrittenBy._id
                        "
                    >
                        <span>
                            {{
                                $t(`reviews.admin.labelManagerOverwrittenBy`, {
                                    fullname: reviewCycle.managerOverwrittenBy.fullname,
                                })
                            }}
                        </span>
                        <span
                            class="pull-right color-grey clickable"
                            tabindex="0"
                            @click="removeManagerOverwrite()"
                            @keyup.enter="removeManagerOverwrite()"
                        >
                            <i class="fal fa-user-minus color-primary"></i>
                        </span>
                    </div>
                </div>
            </div>

            <span
                v-for="(rule, index) in reviewCycle.participationRules.include.users"
                :key="rule._id"
            >
                <span
                    class="label label-success clickable r-default-margin-s"
                    tabindex="0"
                    @click="removeParticipationRule('include', 'users', index)"
                    @keyup.enter="removeParticipationRule('include', 'users', index)"
                >
                    {{ rule.name }}
                    <i class="fal fa-times l-default-margin-s"></i>
                </span>
            </span>
        </div>

        <div
            v-show="
                reviewCycle.createdAsRole == 'employee' &&
                    reviewCycle.managerReviewsEnabled &&
                    reviewCycle.managerOverwritten
            "
        >
            <hr />

            <h4 class="reviewSetupHeading color-dark">
                <strong>{{ $t(`reviews.admin.placeholderManagerOverwrite`) }}</strong>
            </h4>

            <div class="row">
                <div class="col-md-4">
                    <l-input-typeahead
                        v-if="
                            !(
                                reviewCycle.managerOverwrittenBy &&
                                reviewCycle.managerOverwrittenBy._id
                            )
                        "
                        style="margin-top: 0px"
                        :label="``"
                        :placeholder="$t(`reviews.admin.placeholderManagerOverwrite`)"
                        :data="users"
                        :on-select="addManagerOverwrite"
                        :on-input-change="onTypeaheadUserInputChange"
                    />
                    <div
                        v-if="
                            reviewCycle.managerOverwrittenBy && reviewCycle.managerOverwrittenBy._id
                        "
                    >
                        <span>
                            {{
                                $t(`reviews.admin.labelManagerOverwrittenBy`, {
                                    fullname: reviewCycle.managerOverwrittenBy.fullname,
                                })
                            }}
                        </span>
                        <span
                            class="pull-right color-grey clickable"
                            tabindex="0"
                            @click="removeManagerOverwrite()"
                            @keyup.enter="removeManagerOverwrite()"
                        >
                            <i class="fal fa-user-minus color-primary"></i>
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <hr />

        <div>
            <h4 class="reviewSetupHeading color-dark">
                <strong>{{ $t(`reviews.admin.labelPreview`) }}</strong>
            </h4>

            <div class="row">
                <div v-show="reviewCycle.createdAsRole !== 'employee'" class="col-md-5">
                    <l-input-typeahead
                        :label="``"
                        :placeholder="$t(`reviews.list.placeholderOwner`)"
                        :data="users"
                        :on-select="changePreviewUser"
                        :on-input-change="typeaheadOnInputChange"
                        display-selected-value
                    />
                </div>
                <div :class="reviewCycle.createdAsRole !== 'employee' ? 'col-md-4' : 'col-md-5'">
                    <l-select
                        v-model="previewRole"
                        style="margin-top: 10px;"
                        :options="optionsPreviewRole"
                    />
                </div>
                <div class="col-md-3">
                    <button
                        class="btn btn-default btn-block"
                        :disabled="!previewUser._id"
                        :class="{ 'btn-raised': previewUser._id }"
                        @click="previewQuestionnaire()"
                    >
                        {{ $t(`reviews.admin.btnPreview`) }}
                    </button>
                </div>
            </div>
        </div>

        <l-modal v-if="showPreviewQuestionnaire" size="l" close-on-backdrop-click @close="goBack">
            <template #header>{{ $t(`reviews.admin.labelPreviewQuestionnaire`) }}</template>
            <template #body>
                <reviews-templates-edit-questions-preview :id="id" @close="goBack" />
            </template>
        </l-modal>

        <hr />

        <h4 class="reviewSetupHeading color-dark">
            <strong>{{ $t(`reviews.admin.cycleTimeline`) }}</strong>
        </h4>

        <span>{{ $t(`reviews.admin.cycleTimelineKickoffOn`) }}</span>
        <span class="r-default-margin-s">
            <strong>{{ reviewCycle.status.startPlan | moment("MMM D, YYYY") }}</strong>
        </span>
        <span
            class="color-primary clickable"
            tabindex="0"
            @click="showTimeline = !showTimeline"
            @keyup.enter="showTimeline = !showTimeline"
        >
            <i class="fal fa-cog r-default-margin-s"></i>
            <span>{{ $t(`reviews.admin.cycleChangeTimeline`) }}</span>
        </span>

        <div v-show="showTimeline">
            <hr />
            <l-reviews-edit-timeline-form :id="id" @updated="updated" />
        </div>

        <hr />

        <div v-show="errorKickoff" class="alert alert-light alert-danger">
            {{ errorKickoff }}
        </div>

        <div class="row">
            <div class="col-md-3 col-md-offset-9">
                <button
                    class="btn btn-primary btn-raised btn-block"
                    :disabled="
                        !reviewCycleSetupProgress.name ||
                            !reviewCycleSetupProgress.scope ||
                            !reviewCycleSetupProgress.questions ||
                            !reviewCycleSetupProgress.visibility ||
                            !reviewCycleSetupProgress.timeline
                    "
                    @click="updateStatus('new', true)"
                >
                    {{ $t(`reviews.admin.btnConfirmSetup`) }}
                </button>
            </div>
        </div>

        <!-- Congrats Modal -->
        <l-modal-congrats
            v-show="showCongratsModal"
            :title="$t(`reviews.admin.createCongratsModalTitle`)"
            :text="
                new Date(reviewCycle.status.startPlan) > new Date()
                    ? $t(`reviews.admin.createCongratsModalText`)
                    : $t(`reviews.admin.createCongratsModalTextKickoffImminent`)
            "
            :btn-text="$t(`reviews.admin.createCongratsModalBtnText`)"
            :image="congratsModalImage"
            @next="
                () => {
                    showCongratsModal = false;
                    $router.push(`/reviews/cycles`);
                }
            "
        />
        <!--/Congrats Modal -->
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import debounce from "lodash.debounce";

import format from "@/utils/format-typeahead";

import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";
import LModal from "@/components/LModal";
import LModalCongrats from "@/components/LModalCongrats";
import LSelect from "@/components/LSelect";
import LText from "@/components/LText";

import LReviewsEditTimelineForm from "@/components/LReviewsEditTimelineForm";

import ReviewsTemplatesEditQuestionsPreview from "@/views/ReviewsTemplatesEditQuestionsPreview";

import congratsModalImage from "@/img/icons/progress.png";

export default {
    components: {
        LInputText,
        LInputTypeahead,
        LModal,
        LModalCongrats,
        LSelect,
        LText,

        LReviewsEditTimelineForm,

        ReviewsTemplatesEditQuestionsPreview,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            reviewCycle: {},
            errorKickoff: null,

            showTimeline: false,
            previewUser: {},
            previewRole: "manager",
            showPreviewQuestionnaire: false,

            showCongratsModal: false,
            congratsModalImage,
        };
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
            getCycleDetails: "reviews/cycles/details",

            usersSearchList: "users/search/list",
        }),

        directReports() {
            return [
                ...this.usersSearchList
                    .filter(
                        user =>
                            user._id === this.user._id ||
                            user.teamRole?.reportsTo?._id === this.user._id,
                    )
                    .map(user => ({
                        _id: user._id,
                        type: "user",
                        firstname: user.firstname,
                        lastname: user.lastname,
                        name: user.firstname + " " + user.lastname,
                        desc: user.teamRole.title,
                        picture: user.picture,
                    })),
            ];
        },

        users() {
            return this.usersSearchList.map(format.users);
        },

        reviewCycleSetupProgress() {
            const name = Boolean(this.reviewCycle.name);
            const participants = Boolean(
                this.reviewCycle.participationRules?.include?.all ||
                    this.reviewCycle.participationRules?.include?.users?.length ||
                    this.reviewCycle.participationRules?.include?.locations?.length ||
                    this.reviewCycle.participationRules?.include?.localTeams?.length,
            );
            const scope = Boolean(
                this.reviewCycle.selfEvaluationsEnabled ||
                    this.reviewCycle.peerReviewsEnabled ||
                    this.reviewCycle.directReportReviewsEnabled ||
                    this.reviewCycle.managerReviewsEnabled,
            );
            const questions = Boolean(this.reviewCycle.questions?.skills);
            const visibility = Boolean(this.reviewCycle.visibility);
            const timeline = Boolean(
                this.reviewCycle.status?.peerNominationsDelay ||
                    this.reviewCycle.status?.peerApprovalDelay ||
                    this.reviewCycle.status?.peerReviewsDelay ||
                    this.reviewCycle.status?.managerReviewsDelay ||
                    this.reviewCycle.status?.conversationsDelay,
            );
            const notifications = Boolean(this.reviewCycle.customNotifications);

            return {
                name,
                participants,
                scope,
                questions,
                visibility,
                timeline,
                notifications,
            };
        },

        optionsPreviewRole() {
            const options = [];

            if (this.reviewCycle.selfEvaluationsEnabled) {
                options.push({
                    value: "reviewee",
                    text: this.$t(`reviews.admin.optionQFilterReviewee`),
                });
            }
            if (this.reviewCycle.managerReviewsEnabled) {
                options.push({
                    value: "manager",
                    text: this.$t(`reviews.admin.optionQFilterManager`),
                });
            }
            if (this.reviewCycle.peerReviewsEnabled) {
                options.push({ value: "peer", text: this.$t(`reviews.admin.optionQFilterPeer`) });
            }
            if (this.reviewCycle.directReportReviewsEnabled) {
                options.push({
                    value: "directReport",
                    text: this.$t(`reviews.admin.optionQFilterDirectReport`),
                });
            }

            return options;
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler() {
                this.reviewCycle = this.getCycleDetails(this.id);

                if (this.reviewCycle.createdAsRole == "employee") {
                    this.previewUser = this.user;
                }
            },
        },
        reviewCycle: {
            deep: true,
            handler: debounce(function handler() {
                this.update();
            }, 1000),
        },
    },
    methods: {
        changePreviewUser(user) {
            this.previewUser = user;
        },
        typeaheadOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query, sort: "default" });
            } else {
                this.$store.dispatch("users/search/reset");
            }
        },

        async previewQuestionnaire() {
            if (!this.previewRole || !this.previewUser?._id) {
                return;
            }

            await this.$store.dispatch({
                type: "reviews/templates/preview",
                payload: {
                    id: this.id,
                    role: this.previewRole,
                    userId: this.previewUser._id,
                },
            });
            this.showPreviewQuestionnaire = true;
        },
        goBack() {
            this.showPreviewQuestionnaire = false;
        },

        updateStatus(newStatus, confirmed) {
            // double check before updating

            this.updateStatusNewStatus = newStatus;

            if (typeof confirmed === "undefined" && newStatus != "draft") {
                this.updateStatusCheck = true;
                return;
            } else if (!confirmed && newStatus != "draft") {
                this.updateStatusCheck = false;
                return;
            } else {
                this.updateStatusCheck = false;
            }

            // set flag

            this.updatingStatus = true;

            // update

            if (this.isTemplate) {
                this.$store
                    .dispatch({
                        type: "reviews/templates/updateStatus",
                        payload: { id: this.id, status: this.updateStatusNewStatus },
                    })
                    .then(() => {
                        // move to confirmation view

                        this.$router.push("/reviews/templates");

                        this.errorKickoff = null;

                        // reset flag

                        this.updatingStatus = false;
                    })
                    .catch(err => {
                        // show error

                        this.errorKickoff = err.response.data.error;
                    });
            } else {
                this.$store
                    .dispatch({
                        type: "reviews/cycles/updateStatus",
                        payload: { id: this.id, status: this.updateStatusNewStatus },
                    })
                    .then(() => {
                        this.$store.dispatch({
                            type: "reviews/assessments/getList",
                            payload: { scope: "admin" },
                        });

                        // move to confirmation view

                        this.showCongratsModal = true;

                        this.errorKickoff = null;

                        // reset flag

                        this.updatingStatus = false;
                    })
                    .catch(err => {
                        // show error

                        this.errorKickoff = err.response.data.error;
                    });
            }
        },

        onTypeaheadParticipantInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query, sort: "reportsSearch" });
            } else {
                this.$store.dispatch({ type: "users/search/reset" });
            }
        },
        addParticipationRule(item, ruleType) {
            // check whether already present and add if not
            if (
                item.type == "user" &&
                !this.reviewCycle.participationRules[ruleType].users.find(
                    user => user._id == item._id,
                )
            ) {
                this.reviewCycle.participationRules[ruleType].users.push(item);
            }

            this.$store.dispatch({ type: "users/search/reset" });
        },
        removeParticipationRule(ruleType, type, index) {
            if (type == "all") {
                this.reviewCycle.participationRules[ruleType].localTeams = [];
                this.reviewCycle.participationRules[ruleType].locations = [];
                this.reviewCycle.participationRules[ruleType].users = [];
            } else {
                this.reviewCycle.participationRules[ruleType][type].splice(index, 1);
            }
        },

        onTypeaheadUserInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch({ type: "users/search/findByName", query });
            } else {
                this.$store.dispatch({ type: "users/search/reset" });
            }
        },
        addManagerOverwrite(newManager) {
            this.reviewCycle.managerOverwrittenBy = {
                _id: newManager._id,
                fullname: newManager.fullname,
            };
        },
        removeManagerOverwrite() {
            this.reviewCycle.managerOverwrittenBy = {};
        },

        async update() {
            this.error = null;

            try {
                await Promise.all([
                    this.$store.dispatch({
                        type: "reviews/cycles/update",
                        payload: {
                            reviewCycle: this.reviewCycle,
                            scope: "basics",
                        },
                    }),
                    this.$store.dispatch({
                        type: "reviews/cycles/update",
                        payload: {
                            reviewCycle: this.reviewCycle,
                            scope: "participants",
                        },
                    }),
                    this.$store.dispatch({
                        type: "reviews/cycles/update",
                        payload: {
                            reviewCycle: this.reviewCycle,
                            scope: "scope",
                        },
                    }),
                ]);
            } catch (err) {
                if (err?.response?.data?.error?.message) {
                    this.error = { message: err?.response?.data?.error?.message };
                }
            }
        },
        updated() {
            this.reviewCycle = this.getCycleDetails(this.id);
        },
    },
};
</script>
